import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-slate max-w-none">
          <h1 className="text-4xl font-bold text-foreground mb-2">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last Updated: January 16, 2025</p>

          <div className="space-y-8 text-foreground">
            <section>
              <h2 className="text-2xl font-bold mb-4">1. Introduction</h2>
              <p className="mb-4">
                ProductWorkshop, Inc. ("ProductWorkshop," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our customer feedback management platform, including our website, applications, and services (collectively, the "Service"). This Privacy Policy applies to all users of the Service, including administrators, employees, and end users.
              </p>
              <p className="mb-4">
                BY USING THE SERVICE, YOU CONSENT TO THE DATA PRACTICES DESCRIBED IN THIS PRIVACY POLICY. If you do not agree with this Privacy Policy, please do not use the Service.
              </p>
              <p className="mb-4">
                We reserve the right to modify this Privacy Policy at any time. We will notify you of material changes by posting the updated policy on our website and updating the "Last Updated" date. Your continued use of the Service after such modifications constitutes your acceptance of the updated Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">2. Information We Collect</h2>

              <h3 className="text-xl font-semibold mb-3 mt-6">2.1 Information You Provide Directly</h3>
              <p className="mb-4">
                We collect information that you voluntarily provide to us when you:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Register for an account:</strong> Name, email address, company name, job title, and password;</li>
                <li><strong>Use the Service:</strong> Feedback, feature requests, comments, attachments, images, and other content you submit;</li>
                <li><strong>Configure settings:</strong> Preferences, notification settings, team member information, and customization data;</li>
                <li><strong>Communicate with us:</strong> Information in support requests, surveys, or correspondence;</li>
                <li><strong>Make payments:</strong> Billing information, including name, billing address, and payment method details (processed by third-party payment processors).</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">2.2 Information Collected Automatically</h3>
              <p className="mb-4">
                When you access or use the Service, we automatically collect certain information, including:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Device Information:</strong> Hardware model, operating system, browser type and version, device identifiers, and mobile network information;</li>
                <li><strong>Log Information:</strong> IP address, access times, pages viewed, links clicked, and the page you visited before navigating to our Service;</li>
                <li><strong>Location Information:</strong> Approximate geographic location based on IP address;</li>
                <li><strong>Usage Information:</strong> Features used, actions taken, time and frequency of use, and interaction data;</li>
                <li><strong>Cookies and Tracking Technologies:</strong> We use cookies, web beacons, pixels, and similar technologies to collect information about your browsing behavior.</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">2.3 Information from Third-Party Sources</h3>
              <p className="mb-4">
                We may receive information about you from third-party sources, including:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Authentication services (e.g., Google, Microsoft) if you choose to connect your account;</li>
                <li>Analytics providers and advertising partners;</li>
                <li>Business partners and resellers;</li>
                <li>Publicly available sources and data enrichment services.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">3. How We Use Your Information</h2>
              <p className="mb-4">
                We use the information we collect for the following purposes:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Provide and maintain the Service:</strong> Create accounts, process transactions, deliver features, and provide customer support;</li>
                <li><strong>Improve and develop the Service:</strong> Analyze usage patterns, conduct research, develop new features, and enhance user experience;</li>
                <li><strong>Communicate with you:</strong> Send administrative messages, product updates, security alerts, marketing communications, and respond to inquiries;</li>
                <li><strong>Ensure security and prevent fraud:</strong> Detect, investigate, and prevent fraudulent transactions, unauthorized access, and other illegal activities;</li>
                <li><strong>Comply with legal obligations:</strong> Respond to legal requests, enforce our Terms of Service, protect our rights, and comply with applicable laws;</li>
                <li><strong>Personalize your experience:</strong> Customize content, recommendations, and advertisements based on your preferences and behavior;</li>
                <li><strong>Analytics and research:</strong> Generate aggregate statistics about Service usage, create de-identified data sets, and conduct market research;</li>
                <li><strong>Marketing and advertising:</strong> Send promotional materials, measure campaign effectiveness, and display targeted advertisements.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">4. Legal Bases for Processing (GDPR)</h2>
              <p className="mb-4">
                If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, we process your personal data based on the following legal grounds:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Contractual necessity:</strong> Processing is necessary to perform our contract with you (i.e., providing the Service);</li>
                <li><strong>Legitimate interests:</strong> Processing is necessary for our legitimate business interests, such as improving the Service, preventing fraud, and ensuring security;</li>
                <li><strong>Consent:</strong> You have provided explicit consent for specific processing activities;</li>
                <li><strong>Legal compliance:</strong> Processing is necessary to comply with legal obligations.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">5. How We Share Your Information</h2>
              <p className="mb-4">
                We may share your information in the following circumstances:
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">5.1 Service Providers and Business Partners</h3>
              <p className="mb-4">
                We share information with third-party service providers who perform services on our behalf, including:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Cloud hosting and infrastructure providers (e.g., Google Cloud Platform, Firebase);</li>
                <li>Payment processors and fraud prevention services;</li>
                <li>Email delivery and communication platforms;</li>
                <li>Analytics and data enrichment providers;</li>
                <li>Customer support and help desk platforms;</li>
                <li>Marketing and advertising partners.</li>
              </ul>
              <p className="mb-4">
                These service providers are contractually obligated to use your information only as necessary to provide services to us and in accordance with this Privacy Policy.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">5.2 Within Your Organization</h3>
              <p className="mb-4">
                If you use the Service as part of an organization, we may share your information with administrators and other authorized users within your organization as necessary to provide the Service.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">5.3 Business Transfers</h3>
              <p className="mb-4">
                We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company. You will be notified via email and/or prominent notice on our Service of any change in ownership or uses of your information.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">5.4 Legal Requirements and Protection</h3>
              <p className="mb-4">
                We may disclose your information if required to do so by law or in response to valid requests by public authorities, including to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Comply with legal processes, court orders, or governmental requests;</li>
                <li>Enforce our Terms of Service and other agreements;</li>
                <li>Protect the rights, property, or safety of ProductWorkshop, our users, or the public;</li>
                <li>Detect, prevent, or address fraud, security, or technical issues;</li>
                <li>Protect against harm to the rights, property, or safety of our users or the public.</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">5.5 Aggregated and De-identified Data</h3>
              <p className="mb-4">
                We may share aggregated, anonymized, or de-identified information that cannot reasonably be used to identify you. Such information is not subject to this Privacy Policy.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">5.6 With Your Consent</h3>
              <p className="mb-4">
                We may share your information for any other purpose with your explicit consent.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">6. Data Retention</h2>
              <p className="mb-4">
                We retain your information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Specific retention periods depend on the type of information and the purposes for which we use it:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Account information:</strong> Retained for the duration of your account plus a reasonable period thereafter for legal and business purposes;</li>
                <li><strong>User Content:</strong> Retained until you or your organization administrator deletes it or your account is terminated;</li>
                <li><strong>Transaction records:</strong> Retained for at least seven (7) years to comply with tax and accounting obligations;</li>
                <li><strong>Log data:</strong> Retained for up to two (2) years for security and analytics purposes;</li>
                <li><strong>Marketing data:</strong> Retained until you opt out or request deletion.</li>
              </ul>
              <p className="mb-4">
                When we no longer need your information, we will securely delete or anonymize it in accordance with our data retention policies and applicable law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">7. Data Security</h2>
              <p className="mb-4">
                We implement appropriate technical and organizational security measures designed to protect your information from unauthorized access, disclosure, alteration, and destruction. These measures include:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Encryption of data in transit using industry-standard TLS/SSL protocols;</li>
                <li>Encryption of sensitive data at rest;</li>
                <li>Regular security assessments and penetration testing;</li>
                <li>Access controls and authentication mechanisms;</li>
                <li>Employee training on data protection and security practices;</li>
                <li>Incident response and breach notification procedures;</li>
                <li>Secure development practices and code reviews.</li>
              </ul>
              <p className="mb-4">
                However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security. You are responsible for maintaining the confidentiality of your account credentials and for any activities that occur under your account.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">8. Your Rights and Choices</h2>

              <h3 className="text-xl font-semibold mb-3 mt-6">8.1 Access and Portability</h3>
              <p className="mb-4">
                You have the right to access your personal information and, in certain circumstances, receive a copy of it in a structured, commonly used, machine-readable format.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">8.2 Correction and Update</h3>
              <p className="mb-4">
                You can update your account information at any time through your account settings. You may also contact us to request corrections to inaccurate or incomplete information.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">8.3 Deletion</h3>
              <p className="mb-4">
                You have the right to request deletion of your personal information, subject to certain exceptions (e.g., legal obligations, fraud prevention). You can delete your account through account settings or by contacting us. Note that deletion may result in loss of access to the Service.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">8.4 Opt-Out of Marketing Communications</h3>
              <p className="mb-4">
                You may opt out of receiving promotional emails by following the unsubscribe instructions in those emails or by updating your email preferences in account settings. Note that you cannot opt out of certain administrative or transactional communications.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">8.5 Cookie Preferences</h3>
              <p className="mb-4">
                Most web browsers are set to accept cookies by default. You can usually modify your browser settings to decline cookies or alert you when cookies are being sent. However, some features of the Service may not function properly without cookies.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">8.6 Do Not Track Signals</h3>
              <p className="mb-4">
                Some browsers include a "Do Not Track" (DNT) feature that signals to websites you visit that you do not want to have your online activity tracked. Our Service does not currently respond to DNT signals.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">8.7 Additional Rights for EEA, UK, and Swiss Residents</h3>
              <p className="mb-4">
                If you are located in the EEA, UK, or Switzerland, you have additional rights under GDPR, including:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Right to object to processing based on legitimate interests;</li>
                <li>Right to restrict processing in certain circumstances;</li>
                <li>Right to withdraw consent at any time (without affecting the lawfulness of processing based on consent before withdrawal);</li>
                <li>Right to lodge a complaint with a supervisory authority.</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">8.8 Additional Rights for California Residents</h3>
              <p className="mb-4">
                If you are a California resident, you have rights under the California Consumer Privacy Act (CCPA), including:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Right to know what personal information we collect, use, disclose, and sell;</li>
                <li>Right to request deletion of personal information;</li>
                <li>Right to opt out of the sale of personal information (we do not sell personal information);</li>
                <li>Right to non-discrimination for exercising your CCPA rights.</li>
              </ul>
              <p className="mb-4">
                To exercise your rights, please contact us at privacy@getproductworkshop.com. We will verify your identity before processing your request.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">9. International Data Transfers</h2>
              <p className="mb-4">
                Your information may be transferred to, stored, and processed in countries other than your country of residence, including the United States, where our servers and service providers are located. These countries may have data protection laws that differ from those in your country.
              </p>
              <p className="mb-4">
                When we transfer personal information from the EEA, UK, or Switzerland to other countries, we implement appropriate safeguards, including:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Standard Contractual Clauses approved by the European Commission;</li>
                <li>Adequacy decisions by the European Commission;</li>
                <li>Other legally approved mechanisms for international data transfers.</li>
              </ul>
              <p className="mb-4">
                By using the Service, you consent to the transfer of your information to countries outside your country of residence.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">10. Children's Privacy</h2>
              <p className="mb-4">
                The Service is not directed to individuals under the age of 16, and we do not knowingly collect personal information from children under 16. If we learn that we have collected personal information from a child under 16, we will take steps to delete such information as quickly as possible. If you believe we have collected information from a child under 16, please contact us immediately at privacy@getproductworkshop.com.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">11. Third-Party Links and Services</h2>
              <p className="mb-4">
                The Service may contain links to third-party websites, applications, or services that are not owned or controlled by ProductWorkshop. This Privacy Policy does not apply to those third-party services. We are not responsible for the privacy practices of third parties. We encourage you to review the privacy policies of any third-party services you access.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">12. Business Customer Data</h2>
              <p className="mb-4">
                If you are using the Service on behalf of an organization (a "Business Customer"), that organization is the data controller of the User Content and personal information submitted to the Service. We process such information as a data processor on behalf of the Business Customer in accordance with our agreement with them.
              </p>
              <p className="mb-4">
                Business Customers are responsible for ensuring that their use of the Service complies with applicable data protection laws and for providing necessary notices and obtaining required consents from their end users. If you are an end user of a Business Customer, please direct privacy inquiries to the Business Customer.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">13. Changes to This Privacy Policy</h2>
              <p className="mb-4">
                We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will post the updated Privacy Policy on this page and update the "Last Updated" date. If we make material changes, we will provide additional notice, such as via email or through a prominent notice on the Service.
              </p>
              <p className="mb-4">
                We encourage you to review this Privacy Policy periodically. Your continued use of the Service after any changes constitutes your acceptance of the updated Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">14. Contact Us</h2>
              <p className="mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us at:
              </p>
              <p className="mb-4">
                ProductWorkshop, Inc.<br />
                Privacy Officer<br />
                Email: privacy@getproductworkshop.com<br />
                Address: [Physical Address]
              </p>
              <p className="mb-4">
                For GDPR-related inquiries, you may also contact our EU representative at: [EU Representative Contact Information]
              </p>
            </section>

            <section className="border-t border-border pt-8 mt-12">
              <p className="text-sm text-muted-foreground">
                By using the ProductWorkshop Service, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
